import { Button, IconButton, TextInput } from '@loomhq/lens';
import classNames from 'classnames';
import React, { Component } from 'react';
import onClickOutside from 'react-onclickoutside';

import { ENTER } from 'constants/keyCodes';
import * as keyCodes from 'constants/keyCodes';

import UserAvatar from 'components/user-avatar';

import './styles.less';

/**
 * Does not support tokenization
 * Takes a list of suggestion objects with each object with the following
 * structure to get the typeahead to display properly:
 * { photo: <url to photo>, text: <main text of entry>, subtext: <subtext of entry> }
 * one of text or subtext is required at the very least
 */

class SearchTypeahead extends Component {
  state = {
    query: '',
    input_err: null,
    visible: true,
    filtered: [],
    selection: 0,
  };

  static defaultProps = {
    returnInputOnEmptySelection: false,
    ctaButtonText: 'Select',
    showCtaButton: true,
    showIconButton: false,
    iconButton: null,
    autoFocus: true,
    // returns null/false for valid queries, otherwise returns error string
    queryValidation: () => false,
    onSelection: () => {},
    blacklist: () => false,
    onFocus: () => {},
    onKeyUp: () => {},
  };

  render() {
    const { query, visible, filtered, selection, input_err } = this.state;
    const {
      ctaButtonText,
      showCtaButton,
      autoFocus,
      iconButton,
      showIconButton,
    } = this.props;

    const showDropdown = query.length > 0 && visible;

    return (
      <div
        className={classNames('loom-search-typeahead', {
          withIconButton: showIconButton,
        })}
      >
        <TextInput
          onKeyDown={this.handleKeys}
          onKeyUp={e => {
            this.props.onKeyUp(e);

            if (e.keyCode === ENTER) {
              this.handleSelection(null);
            }
          }}
          ref={me => (this.editor = me)}
          autoFocus={autoFocus}
          value={query}
          hasError={input_err}
          onFocus={this.props.onFocus}
          onChange={e => this.updateQuery(e.target.value)}
        />
        {showIconButton && (
          <IconButton
            altText={ctaButtonText}
            icon={iconButton}
            size="medium"
            onClick={() => this.handleSelection(null, true)}
          />
        )}
        {showCtaButton && (
          <div className="pl:small">
            <Button
              isDisabled={query.length < 1}
              onClick={() => this.handleSelection(null, true)}
              variant="primary"
            >
              {ctaButtonText}
            </Button>
          </div>
        )}
        {showDropdown && (
          <div className="loom-search-typeahead-dropdown ignore-react-onclickoutside">
            {filtered.map((row, idx) => {
              const { text, subtext, photo } = row;
              const shouldHighlight = selection === idx;

              const avatar = (
                <div className="mr:small">
                  <UserAvatar
                    avatarSize={4}
                    avatarSrc={photo}
                    name={text || subtext}
                  />
                </div>
              );

              return (
                <div
                  key={row.subtext}
                  onClick={() => this.handleSelection(idx)}
                  onMouseEnter={() => this.setState({ selection: idx })}
                  className={`loom-search-typeahead-dropdown-item${
                    shouldHighlight ? ' selected' : ''
                  }`}
                >
                  {avatar}
                  <div className="typeahead-dropdown-item-content">
                    <div className="typeahead-dropdown-item-text">
                      {text || subtext}
                    </div>
                    <div className="typeahead-dropdown-item-subtext">
                      {subtext}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  updateQuery = query => this.setState({ query, input_err: false });

  componentDidUpdate(prevProps, prevState) {
    if (prevState.query !== this.state.query) {
      const { suggestions } = this.props;
      const { query } = this.state;

      this.setState({
        visible: true,
        filtered: suggestions.filter(entry => {
          if (this.props.blacklist(entry)) {
            return false;
          }

          const lQuery = query.toLowerCase();
          const lText = entry.text.toLowerCase();
          const lSubText = entry.subtext.toLowerCase();

          // we do prefix matching for each individual word and whole phrases
          return (
            lText.startsWith(lQuery) ||
            lSubText.startsWith(lQuery) ||
            lText.split(' ').some(part => part.startsWith(lQuery)) ||
            lSubText.split(' ').some(part => part.startsWith(lQuery))
          );
        }),
      });
    }
  }

  handleSelection = async (idx = null, forceQuery = false) => {
    const { filtered, selection, query } = this.state;
    const { returnInputOnEmptySelection } = this.props;
    const invalidQuery = await this.props.queryValidation(query.toLowerCase());

    if (!invalidQuery || filtered.length > 0) {
      this.setState({ query: '', filtered: [], selection: 0 });
    }

    if (returnInputOnEmptySelection && (filtered.length === 0 || forceQuery)) {
      if (!invalidQuery) {
        this.props.onSelection({ text: query });
      } else {
        this.setState({ input_err: invalidQuery });
      }

      return;
    }

    const filteredSelection = filtered[idx || selection].subtext;

    this.props.onSelection({
      text: filteredSelection,
    });
  };

  handleKeys = e => {
    const { keyCode } = e;
    const { selection, filtered } = this.state;

    switch (keyCode) {
      case keyCodes.ARROW_UP:
        this.setState({ selection: Math.max(selection - 1, 0) });

        break;
      case keyCodes.ARROW_DOWN:
        this.setState({
          selection: Math.min(selection + 1, filtered.length - 1),
        });

        break;
      default:
        return;
    }

    e.preventDefault();
  };

  handleClickOutside() {
    if (!this.state.visible) {
      return;
    }

    this.setState({ visible: false });
  }
}

export default onClickOutside(SearchTypeahead);
